import React, { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import languageLocale from 'utils/LanguageLocale';
import ProductCard from 'components/Product/ProductCard';
import AddToBasketButton from 'components/AddToBasketButton';
import basketUtils from 'utils/Basket';
import {
    Grid, Text, Box, Divider, Icon, Flex
} from 'components/ui';
import mediaUtils from 'utils/Media';
import helpersUtils from 'utils/Helpers';
import analyticsConsts from 'analytics/constants';
import { CONSTRUCTOR_PODS, RESULTS_COUNT } from 'constants/constructorConstants';
import FrequentlyBoughtTogetherEN from 'components/ProductPage/FrequentlyBoughtTogether/locales/FrequentlyBoughtTogether_en_US';
import FrequentlyBoughtTogetherFR from 'components/ProductPage/FrequentlyBoughtTogether/locales/FrequentlyBoughtTogether_fr_CA';
import ConstructorRecsActions from 'actions/ConstructorRecsActions';

const { ADD_TO_BASKET_TYPES } = basketUtils;
const { isMobileView } = mediaUtils;
const { calculateTotalPrice } = helpersUtils;
const { updateRequestData } = ConstructorRecsActions;
const getText = languageLocale.getLocalisedDataFromResource(FrequentlyBoughtTogetherEN, FrequentlyBoughtTogetherFR);

const EMPTY_RECS = { recs: [], title: '' };

const selectFBTRecs = createSelector(
    state => state.constructorRecommendations.constructorRecommendations,
    allRecs => {
        const currentRecs = (allRecs || {})[CONSTRUCTOR_PODS.FREQUENTLY_BOUGHT_TOGETHER];

        if (!currentRecs?.isError && !currentRecs?.isEmpty) {
            const { constructorRecommendations: recs = [], constructorTitle: title = '', totalResults, resultId } = currentRecs || {};

            return { recs, title, totalResults, resultId };
        }

        return EMPTY_RECS;
    }
);

const buildAddProps = recommendedSKUs => ({
    variant: ADD_TO_BASKET_TYPES.PRIMARY,
    sku: recommendedSKUs || [],
    text: getText('addAllToBasket'),
    hasMinWidth: true,
    isAddButton: true,
    showBasketQuickAdd: false,
    isQuickLook: false,
    analyticsContext: analyticsConsts.CONTEXT.FREQUENTLY_BOUGHT_TOGETHER,
    rootContainerName: analyticsConsts.CONTEXT.FREQUENTLY_BOUGHT_TOGETHER,
    isMultiProductsAdd: true
});

// eslint-disable-next-line complexity
const FrequentlyBoughtTogether = ({ currentProduct, shouldExcludeCurrentProduct, showAddAllToBasketButton = true, isCarousel }) => {
    const dispatch = useDispatch();
    const { recs, title, totalResults, resultId } = useSelector(selectFBTRecs, shallowEqual);

    const productId = currentProduct?.productDetails?.productId;

    useEffect(() => {
        const params = { itemIds: productId, numResults: RESULTS_COUNT.FREQUENTLY_BOUGHT_TOGETHER };
        dispatch(updateRequestData({ params, podId: CONSTRUCTOR_PODS.FREQUENTLY_BOUGHT_TOGETHER }));
    }, [dispatch, productId]);

    const recommendedSKUs = shouldExcludeCurrentProduct ? recs.slice(1) : recs;

    if (!recommendedSKUs?.length) {
        return null;
    }

    const addProps = showAddAllToBasketButton ? buildAddProps(recommendedSKUs) : {};

    const totalPrice = `${getText('totalPrice', [calculateTotalPrice(recommendedSKUs)])}`;

    return (
        <Box
            marginBottom={showAddAllToBasketButton ? [6, 8] : 0}
            lineHeight='tight'
            {...(!showAddAllToBasketButton && { marginTop: 4 })}
        >
            <Divider />
            <Text
                is='h2'
                marginTop='1em'
                marginBottom={[3, 5]}
                fontSize={showAddAllToBasketButton ? ['md', 'lg'] : 'md'}
                fontWeight='bold'
                children={showAddAllToBasketButton ? title : getText('frequentlyBoughtWithThisProduct')}
                {...(!showAddAllToBasketButton && { textAlign: 'center' })}
            />
            <Grid
                backgroundColor={showAddAllToBasketButton ? 'nearWhite' : 'unset'}
                alignItems='start'
                gap={[showAddAllToBasketButton ? 4 : 0, null, null, showAddAllToBasketButton ? 5 : 0]}
                columns={[null, null, null, '1fr auto']}
                marginX={['-container', null, null, 0]}
                paddingY={[4, null, null, showAddAllToBasketButton ? 5 : 4]}
                paddingX={[3, null, null, showAddAllToBasketButton ? 5 : 3]}
                borderRadius={[null, null, null, 2]}
            >
                <Box
                    overflow={shouldExcludeCurrentProduct ? ['auto', null, null, 'unset'] : 'auto'}
                    padding={[3, null, null, 5]}
                    margin={[-3, null, null, -5]}
                    {...(!showAddAllToBasketButton && {
                        marginLeft: [null, -2],
                        marginTop: [-5, -5],
                        paddingLeft: [null, null, null, 3],
                        paddingRight: [null, null, null, 3],
                        paddingTop: [null, 1, 1, 1]
                    })}
                >
                    <Flex
                        minWidth={shouldExcludeCurrentProduct ? [recommendedSKUs.length === 1 ? 287 : 583] : [901, null, null, 893]}
                        {...(!showAddAllToBasketButton && { paddingLeft: 1, paddingRight: [1, 2, null, 1] })}
                        data-cnstrc-recommendations
                        data-cnstrc-recommendations-pod-id={CONSTRUCTOR_PODS.FREQUENTLY_BOUGHT_TOGETHER}
                        data-cnstrc-result-id={resultId}
                        data-cnstrc-num-results={totalResults}
                    >
                        {recommendedSKUs.map((item, index) => (
                            <React.Fragment
                                key={item.skuId}
                                data-cnstrc-recommendations
                                data-cnstrc-recommendations-pod-id={CONSTRUCTOR_PODS.FREQUENTLY_BOUGHT_TOGETHER}
                                data-cnstrc-result-id={resultId}
                                data-cnstrc-num-results={totalResults}
                            >
                                {index ? (
                                    showAddAllToBasketButton ? (
                                        <Icon
                                            name='plus'
                                            size='12px'
                                            marginX={1}
                                            css={{ alignSelf: 'center' }}
                                        />
                                    ) : (
                                        <Box marginX={1} />
                                    )
                                ) : null}
                                <ProductCard
                                    isSkeleton={false}
                                    sku={item.sku || item}
                                    showPrice={true}
                                    showAddButton={true}
                                    useInternalTracking={true}
                                    showMarketingFlags={true}
                                    showLovesButton={false}
                                    showRating={true}
                                    isHorizontal={true}
                                    imageSize={[60, null, null, 80]}
                                    rootContainerName={analyticsConsts.CONTEXT.FREQUENTLY_BOUGHT_TOGETHER}
                                    analyticsContext={analyticsConsts.CONTEXT.FREQUENTLY_BOUGHT_TOGETHER}
                                    isCarousel={isCarousel}
                                />
                            </React.Fragment>
                        ))}
                    </Flex>
                </Box>
                <Grid
                    gap={4}
                    alignItems='center'
                    columns={['auto 1fr', null, null, 1]}
                    order={[-1, null, null, 0]}
                    marginLeft={[1, null, null, 0]}
                >
                    {showAddAllToBasketButton && (
                        <Text
                            is='h3'
                            fontWeight='bold'
                            children={totalPrice}
                        />
                    )}
                    <AddToBasketButton
                        {...addProps}
                        size={isMobileView() ? 'sm' : undefined}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default wrapFunctionalComponent(FrequentlyBoughtTogether, 'FrequentlyBoughtTogether');
